/* shellback, facing you — the same breath, with the head out.
 *
 * A shellback commits to a direction: its armour blocks from the front, and
 * `Enemy.draw` already offsets a hard shield bar the way it is facing. The
 * BODY did not agree with the bar, because there was one body. This is that
 * body from the front, and it is the same shell — the ink of every frame and
 * the whole open/shut cycle are `shellback.ts`'s, so the creature does not
 * change identity when it turns.
 *
 * THE OUTLINE IS NOT. It used to be `shellback.ts`'s byte for byte, and that
 * was the defect: measured, every shellback pair differed by EXACTLY 0.0px of
 * silhouette across all eight frames, against 46.1 for charger and 39.9 for
 * forager. All 24 authored frames were one blob with the interior repainted,
 * and `tools/spritecheck.ts` could not see it because all five of its
 * statistics are computed on character differences -- a step that keeps every
 * lit cell lit and repaints inside scores identically to one that moved a limb.
 *
 * Head-on, the shell is BROADER and SQUATTER than in profile: the crown row
 * comes off, the shoulders grow a cell either way, and the head shows in the
 * silhouette under the front lip rather than only in the ink -- wide on the
 * open frames, narrow on the shut ones, which is the same movement the eyes
 * already make. 19.0px a frame against the side, and 36.8 against the rear.
 * Below the charger's 46 on purpose: a charger has limbs to swing and this is
 * a shell that fills the grid's full width already.
 *
 * THE HEAD IS THE WHOLE DIFFERENCE, AND IT BREATHES WITH THE SHELL. It sits
 * in the rim row, which is where a head-on head would be — at the ground,
 * under the front lip — and it is out when the shell is open and drawn in
 * when the shell shuts: six pixels wide with two `e` eyes on the open frames
 * (0, 7), four wide with two eyes on the settling frames (1, 2, 6), and four
 * wide with NO eyes on the three shut frames (3, 4, 5). The armour closing
 * and the head going in are one movement, which is the thing about this
 * creature worth animating.
 *
 * Nothing is added to make that work: the rim row was sixteen lit pixels in
 * every frame already, so putting a head in it is a change of colour and not
 * of mass. Ink is identical to the side view frame for frame.
 *
 * THE SHELL SETTLES, IT DOES NOT DEFLATE (2026-09-06). The header above used to claim ink
 * 128/126/124/122/116/122/126/128 and a swing of 9.5%. The art did not match it: measured off the grids
 * themselves the frames were 132/118/118/88/88/88/118/132 -- a swing of 37.3% -- because the shut frames lost two
 * rows and two columns, so the whole animal shrank and swelled instead of breathing. A shellback's shell is armour;
 * it is the one part that cannot compress. The shut frames keep the full footprint now, and the two widest came
 * down a little so the loop sits inside its ten per cent.
 *
 * Measured: ink 130/118/118/126/120/126/118/130 (median 130, swing 9.5%), steps 24/18/44/50/36/24/18/22 px,
 * spread 2.1x the median and 2.8x the smallest, floor row 15 in all eight frames.
 */

// phase 0 — open, arched, nine rows, and the head fully out: six pixels of
// muzzle across the front lip with both eyes lit.
export const shellback_down_0 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '..oonnnnnnnnoo..',
  'oonNNNNNNNNNNnoo',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNNNNNNNNNNno',
  'oonneennnneennoo',
  'ooooonneennooooo',
  '.....onnnno.....',
];

// phase 1 — the shoulder is swallowed and the shell lands wide; the muzzle
// narrows to four but the eyes are still showing.
export const shellback_down_1 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  'onNNNNNNNNNNNNno',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNNnnnnnnnnNNno',
  'onNNNNNNNNNNNNno',
  'ooneennnnnneenoo',
  'ooooooneenoooooo',
  '.....onnnno.....',
];

// phase 2 — the plates slide and the seam pinches. The head holds where it is.
export const shellback_down_2 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  'onNNNNNNNNNNNNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNnnnnnnNNNno',
  'onNNNNNNNNNNNNno',
  'ooneennnnnneenoo',
  'ooooooneenoooooo',
  '.....onnnno.....',
];

// phase 3 — the shoulder pushes back out and the lowest band goes. The eyes
// close: from here the shell is shutting and there is nothing to see.
export const shellback_down_3 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '...onnnnnnnno...',
  'oonNNNNNNNNNNnoo',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNNNNNNNNNNno',
  'oonneennnneennoo',
  'ooooonneennooooo',
  '......onno......',
];

// phase 4 — shut. The tightest, lightest frame of the loop and the one a hit
// bounces off, with the head blind behind the lip.
export const shellback_down_4 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '......oooo......',
  'oonNNNNNNNNNNnoo',
  'onNNNNNNNNNNNNno',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'oonnneenneennnoo',
  'ooooonneennooooo',
  '......onno......',
];

// phase 5 — the crack. The interior opens before the outline does, and the
// head is still in.
export const shellback_down_5 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '...onnnnnnnno...',
  'oonNNNNNNNNNNnoo',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNnnnnnnnnNNno',
  'onNNNNNNNNNNNNno',
  'oonneennnneennoo',
  'ooooonneennooooo',
  '......onno......',
];

// phase 6 — widest and flattest, the push-off frame; the eyes come back.
export const shellback_down_6 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  'onNNNNNNNNNNNNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNnnnnnnnnNNno',
  'onNNNNNNNNNNNNno',
  'oonnneenneennnoo',
  'ooooooneenoooooo',
  '.....onnnno.....',
];

// phase 7 — up. Nine rows again and the muzzle back out to six. Next: open.
export const shellback_down_7 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '..oonnnnnnnnoo..',
  'oonNNNNNNNNNNnoo',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNnnnnnnnnNNno',
  'onNNNNNNNNNNNNno',
  'oonnneenneennnoo',
  'ooooonneennooooo',
  '.....onnnno.....',
];
